import * as documentsServices from 'services/api/documents';
import { getErrorMessage, parsePagination, isCancel } from 'utils';
import { uiActions } from 'state/modules/UI';
import * as types from './types';
import i18n from 'i18n-js';
import moment from 'moment';

// CHANGE DOCUMENT STATUS
const fetchDocumentsByIdRequest = () => ({
  type: types.FETCH_DOCUMENTS_BY_ID_REQUEST,
});

const fetchDocumentsByIdSuccess = (payload) => ({
  type: types.FETCH_DOCUMENTS_BY_ID_SUCCESS,
  payload,
});

const fetchDocumentsByIdFailed = () => ({
  type: types.FETCH_DOCUMENTS_BY_ID_FAILED,
});

export const fetchDocumentsById = (params) => {
  return async (dispatch) => {
    dispatch(fetchDocumentsByIdRequest());
    const response = await documentsServices.fetchDocuemntsById(params);

    if (response.ok) {
      return dispatch(
        fetchDocumentsByIdSuccess({
          sourceId: params.source_id,
          data: response.data,
        })
      );
    }

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response.data),
      })
    );
    dispatch(fetchDocumentsByIdFailed());
  };
};

// CHANGE DOCUMENT STATUS
const fetchChangeDocumentStatusRequest = () => ({
  type: types.FETCH_UPDATE_DOCUMENT_REQUEST,
});

const fetchChangeDocumentStatusSuccess = (payload) => ({
  type: types.FETCH_UPDATE_DOCUMENT_SUCCESS,
  payload,
});

const fetchChangeDocumentStatusFailed = () => ({
  type: types.FETCH_UPDATE_DOCUMENT_FAILED,
});

export const fetchChangeDocumentStatus = (id, payload) => {
  return async (dispatch, getState) => {
    const documentsList = getState().documents.list;
    dispatch(fetchChangeDocumentStatusRequest());
    const response = await documentsServices.fetchUpdateDocument(id, payload);
    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'info',
          message: `${i18n.t('documents.success_status')} ${payload.status}`,
        })
      );
      const documentIndex = documentsList.findIndex((el) => el.id === id);
      let list = [...documentsList];
      list[documentIndex] = response.data;
      return dispatch(fetchChangeDocumentStatusSuccess(list));
    }
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response.data),
      })
    );

    dispatch(fetchChangeDocumentStatusFailed());
  };
};

// UPLOAD DOCUMENT
const fetchUploadDocumentRequest = () => ({
  type: types.FETCH_UPLOAD_DOCUMENT_REQUEST,
});

const fetchUploadDocumentSuccess = (payload) => ({
  type: types.FETCH_UPLOAD_DOCUMENT_SUCCESS,
  payload,
});

const fetchUploadDocumentFailed = () => ({
  type: types.FETCH_UPLOAD_DOCUMENT_FAILED,
});

export const fetchUploadDocument = (payload) => {
  return async (dispatch, getState) => {
    const documentsList = getState().documents.list;
    dispatch(fetchUploadDocumentRequest());
    const response = await documentsServices.fetchUploadDocument(payload);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('documents.success_upload'),
        })
      );
      const documentIndex = documentsList.findIndex((el) => el.id === response?.data.id);
      let list = [...documentsList];
      list[documentIndex] = response.data;
      return dispatch(fetchUploadDocumentSuccess(list));
    }

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response.data),
      })
    );

    dispatch(fetchUploadDocumentFailed());
  };
};

export const resetUploadDocument = () => (dispatch) => {
  dispatch({
    type: types.FETCH_UPLOAD_DOCUMENT_FAILED,
  });
};

// DOWNLOAD FILE
const fetchDonwloadDocumentRequest = () => ({
  type: types.FETCH_DOWNLOAD_DOCUMENT_REQUEST,
});

const fetchDonwloadDocumentSuccess = (payload) => ({
  type: types.FETCH_DOWNLOAD_DOCUMENT_SUCCESS,
  payload,
});

const fetchDonwloadDocumentFailed = () => ({
  type: types.FETCH_DOWNLOAD_DOCUMENT_FAILED,
});

export const fetchDonwloadDocument = (id) => {
  return async (dispatch) => {
    dispatch(fetchDonwloadDocumentRequest());
    const response = await documentsServices.fetchDownloadDocument(id);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('documents.success_generate_docs'),
        })
      );
      const blob = new Blob([response.data], {
        type: 'application/octet-stream',
      });
      return dispatch(fetchDonwloadDocumentSuccess(blob));
    }

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response.data),
      })
    );

    dispatch(fetchDonwloadDocumentFailed());
  };
};

export const resetFile = () => (dispatch) => {
  dispatch({
    type: types.RESET_FILE,
  });
};

export const createVersionRequest = () => ({
  type: types.CREATE_VERSION_REQUEST,
});

export const createVersionRequestSuccess = () => ({
  type: types.CREATE_VERSION_SUCCESS,
});

export const createVersionFailure = (payload) => ({
  type: types.CREATE_VERSION_FAILURE,
  payload,
});

export const createVersion = (documentId, payload) => async (dispatch) => {
  let data = new FormData();
  Object.keys(payload).map((key) => {
    if (key === 'expiration_time') {
      data.append(key, moment(payload[key]).format());
    }
    return data.append(key, payload[key]);
  });

  dispatch(createVersionRequest());
  const response = await documentsServices.createVersion(documentId, data);
  if (response.ok) {
    dispatch(createVersionRequestSuccess(response.data));
  } else {
    dispatch(createVersionFailure(response.data));
  }
  return response;
};

const createDocumentRequest = () => ({
  type: types.CREATE_DOCUMENT_REQUEST,
});

const createDocumentRequestSuccess = () => ({
  type: types.CREATE_DOCUMENT_SUCCESS,
});

const createDocumentFailure = () => ({
  type: types.CREATE_DOCUMENT_FAILURE,
});

export const createDocument = (payload) => async (dispatch) => {
  dispatch(createDocumentRequest());
  const response = await documentsServices.createDocument(payload);
  if (response.ok) {
    dispatch(createDocumentRequestSuccess(response.data));
    return response;
  }
  const error = Array.isArray(response?.data?.details)
    ? response?.data?.details
    : getErrorMessage(response.data);
  dispatch(createDocumentFailure());
  dispatch(
    uiActions.handleShowSnackbar({
      type: 'error',
      message: getErrorMessage(error),
    })
  );
  return response;
};

const normalizeDocuments = (data = []) => {
  let byId = {};
  let all = [];
  data.map((doc) => {
    all.push(doc.id);
    return (byId[doc.id] = doc);
  });
  return { byId, all };
};

const fetchDocumentsRequest = () => ({
  type: types.FETCH_DOCUMENTS_REQUEST,
});

const fetchDocumentsSuccess = (payload) => ({
  type: types.FETCH_DOCUMENTS_SUCCESS,
  payload,
});

const fetchDocumentsFailure = () => ({
  type: types.FETCH_DOCUMENTS_FAILURE,
});

export const fetchDocuments = (params, options) => async (dispatch) => {
  dispatch(fetchDocumentsRequest());
  const response = await documentsServices.fetchDocuments(params, options);
  if (response.ok) {
    const pagination = parsePagination(response.headers);
    dispatch(
      fetchDocumentsSuccess({
        pagination,
        data: normalizeDocuments(response.data),
      })
    );
    return;
  }
  if (!isCancel(response)) {
    dispatch(fetchDocumentsFailure());
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response.data),
      })
    );
  }
};

const fetchDocumentRequest = () => ({
  type: types.FETCH_DOCUMENT_REQUEST,
});

const fetchDocumentSuccess = (payload) => ({
  type: types.FETCH_DOCUMENT_SUCCESS,
  payload,
});

const fetchDocumentFailure = () => ({
  type: types.FETCH_DOCUMENT_FAILURE,
});

export const fetchDocument = (id) => async (dispatch) => {
  try {
    dispatch(fetchDocumentRequest());
    const response = await documentsServices.fetchDocument(id);
    dispatch(fetchDocumentSuccess({ id, data: response.data }));
  } catch (error) {
    dispatch(fetchDocumentFailure());
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(error),
      })
    );
  }
};

const fetchVersionRequest = () => ({
  type: types.FETCH_VERSION_REQUEST,
});

const fetchVersionSuccess = (payload) => ({
  type: types.FETCH_VERSION_SUCCESS,
  payload,
});

const fetchVersionFailure = () => ({
  type: types.FETCH_VERSION_FAILURE,
});

export const fetchVersion = (documentId, versionId) => async (dispatch) => {
  try {
    dispatch(fetchVersionRequest());
    const response = await documentsServices.fetchVersion(documentId, versionId);
    dispatch(fetchVersionSuccess({ documentId, ...response.data }));
  } catch (error) {
    dispatch(fetchVersionFailure());
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(error),
      })
    );
  }
};

const updateVersionRequest = () => ({
  type: types.UPDATE_VERSION_REQUEST,
});

const updateVersionSuccess = (payload) => ({
  type: types.UPDATE_VERSION_SUCCESS,
  payload,
});

const updateVersionFailure = () => ({
  type: types.UPDATE_VERSION_FAILURE,
});

export const updateVersion =
  (documentId, versionId, payload, onSuccess, showFeedback) => async (dispatch) => {
    dispatch(updateVersionRequest());
    let data = new FormData();
    Object.keys(payload).map((key) => {
      if (key === 'expiration_time') {
        data.append(key, moment(payload[key]).format());
      }
      return data.append(key, payload[key]);
    });
    const response = await documentsServices.updateVersion(documentId, versionId, data);
    if (response.ok) {
      if (onSuccess) {
        onSuccess();
      }
      if (showFeedback) {
        dispatch(
          uiActions.handleShowSnackbar({
            type: 'success',
            message: i18n.t('documents.success_update_version'),
          })
        );
      }
      dispatch(updateVersionSuccess({ documentId, ...response.data }));
    } else {
      dispatch(updateVersionFailure());
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: getErrorMessage(response.data),
        })
      );
    }
    return response;
  };

const updateDocumentRequest = () => ({
  type: types.UPDATE_DOCUMENT_REQUEST,
});

const updateDocumentSuccess = (payload) => ({
  type: types.UPDATE_DOCUMENT_SUCCESS,
  payload,
});

const updateDocumentFailure = () => ({
  type: types.UPDATE_DOCUMENT_FAILURE,
});

export const updateDocument = (documentId, versionId) => async (dispatch) => {
  dispatch(updateDocumentRequest());
  const response = await documentsServices.updateDocument(documentId, versionId);
  if (response?.ok) {
    dispatch(updateDocumentSuccess({ documentId, ...response.data }));
  } else {
    dispatch(updateDocumentFailure());
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response?.data),
      })
    );
  }
};

// GET VERSIONS OF THE CURRENT DOCUMENT
const fetchVersionsByDocumentIdRequest = () => ({
  type: types.FETCH_VERSION_BY_DOCUMENT_REQUEST,
});

const fetchVersionsByDocumentIdSuccess = (payload) => ({
  type: types.FETCH_VERSION_BY_DOCUMENT_SUCCESS,
  payload,
});

const fetchVersionsByDocumentIdFailed = () => ({
  type: types.FETCH_VERSION_BY_DOCUMENT_FAILED,
});

export const fetchVersionsByDocumentId = (documentId, params) => {
  return async (dispatch) => {
    dispatch(fetchVersionsByDocumentIdRequest());

    const response = await documentsServices.fetchVersionsByDocumentId(documentId, params);

    if (response.ok) {
      const pagination = parsePagination(response.headers);
      return dispatch(
        fetchVersionsByDocumentIdSuccess({
          pagination,
          data: response.data,
        })
      );
    }
    const error = Array.isArray(response?.data?.details)
      ? response?.data?.message
      : getErrorMessage(response.data);
    dispatch(fetchVersionsByDocumentIdFailed());
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );
  };
};

const deleteVersionsFileRequest = () => ({
  type: types.DELETE_VERSIONS_FILE_REQUEST,
});

const deleteVersionsFileSuccess = (payload) => ({
  type: types.DELETE_VERSIONS_FILE_SUCCESS,
  payload,
});

const deleteVersionsFileFailure = () => ({
  type: types.DELETE_VERSIONS_FILE_FAILURE,
});

export const deleteVersionsFile = (documentId, versionId) => async (dispatch) => {
  try {
    dispatch(deleteVersionsFileRequest());
    const response = await documentsServices.deleteVersionsFile(documentId, versionId);
    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: 'Image deleted successfully',
        })
      );
      return dispatch(deleteVersionsFileSuccess({ documentId, ...response.data }));
    }
    dispatch(deleteVersionsFileFailure());
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response.data),
      })
    );
  } catch (error) {
    dispatch(deleteVersionsFileFailure());
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(error),
      })
    );
  }
};

const uploadVersionsFileRequest = () => ({
  type: types.UPLOAD_VERSIONS_FILE_REQUEST,
});

const uploadVersionsFileSuccess = (payload) => ({
  type: types.UPLOAD_VERSIONS_FILE_SUCCESS,
  payload,
});

const uploadVersionsFileFailure = () => ({
  type: types.UPLOAD_VERSIONS_FILE_FAILURE,
});

export const uploadVersionsFile = (documentId, versionId, file) => {
  return async (dispatch) => {
    dispatch(uploadVersionsFileRequest());

    let data = new FormData();
    data.append('data', file);
    const response = await documentsServices.uploadVersionsFile(documentId, versionId, data);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: 'Image uploaded successfully',
        })
      );
      return dispatch(uploadVersionsFileSuccess({ documentId, ...response.data }));
    }

    const error = getErrorMessage(response.data);

    dispatch(uploadVersionsFileFailure());
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(error),
      })
    );
  };
};

const deleteVersionRequest = () => ({
  type: types.FETCH_DELETE_VERSION_REQUEST,
});

const deleteVersionSuccess = (payload) => ({
  type: types.FETCH_DELETE_VERSION_SUCCESS,
  payload,
});

const deleteVersionFailure = () => ({
  type: types.FETCH_DELETE_VERSION_FAILED,
});

export const deleteVersion = (documentId, versionId, onSuccess) => async (dispatch) => {
  dispatch(deleteVersionRequest());
  const response = await documentsServices.deleteVersion(documentId, versionId);

  if (response.ok) {
    if (onSuccess) {
      onSuccess();
    }
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'success',
        message: i18n.t('documents.success_deleted_version'),
      })
    );
    return dispatch(deleteVersionSuccess({ documentId, versionId }));
  }
  dispatch(deleteVersionFailure());
  dispatch(
    uiActions.handleShowSnackbar({
      type: 'error',
      message: getErrorMessage(response.data),
    })
  );
};

const fetchVersionByIdRequest = () => ({
  type: types.FETCH_VERSION_BY_ID_REQUEST,
});

const fetchVersionByIdSuccess = (payload) => ({
  type: types.FETCH_VERSION_BY_ID_SUCCESS,
  payload,
});

const fetchVersionByIdFailure = () => ({
  type: types.FETCH_VERSION_BY_ID_FAILURE,
});

export const fetchVersionById = (documentId, versionId) => {
  return async (dispatch) => {
    dispatch(fetchVersionByIdRequest());

    const response = await documentsServices.fetchVersion(documentId, versionId);

    if (response.ok) {
      return dispatch(fetchVersionByIdSuccess(response));
    }

    const error = getErrorMessage(response.data);

    dispatch(fetchVersionByIdFailure());
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(error),
      })
    );
  };
};

const deleteDocumentRequest = () => ({
  type: types.DELETE_DOCUMENT_REQUEST,
});

const deleteDocumentSuccess = (payload) => ({
  type: types.DELETE_DOCUMENT_SUCCESS,
  payload,
});

const deleteDocumentFailure = () => ({
  type: types.DELETE_DOCUMENT_FAILURE,
});

export const deleteDocument = (documentId, onSuccess) => async (dispatch) => {
  try {
    dispatch(deleteDocumentRequest());
    await documentsServices.deleteDocument(documentId);
    if (onSuccess) {
      onSuccess();
    }
    dispatch(deleteDocumentSuccess({ documentId }));
  } catch (error) {
    dispatch(deleteDocumentFailure());
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(error),
      })
    );
  }
};

const fetchDocumentTypesRequest = () => ({
  type: types.FETCH_DOCUMENT_TYPES_REQUEST,
});

const fetchDocumentTypesSuccess = (payload) => ({
  type: types.FETCH_DOCUMENT_TYPES_SUCCESS,
  payload,
});

const fetchDocumentTypesFailure = () => ({
  type: types.FETCH_DOCUMENT_TYPES_FAILURE,
});

export const fetchDocumentTypes = (params, options) => async (dispatch) => {
  dispatch(fetchDocumentTypesRequest());
  const response = await documentsServices.fetchDocumentTypes(params, options);
  if (response.ok) {
    const pagination = parsePagination(response.headers);
    dispatch(
      fetchDocumentTypesSuccess({
        pagination,
        data: response.data,
      })
    );
    return;
  }

  if (!isCancel(response)) {
    dispatch(fetchDocumentTypesFailure());
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response.data),
      })
    );
  }
};

const updateDocumentTypeRequest = () => ({
  type: types.UPDATE_DOCUMENT_TYPE_REQUEST,
});

const updateDocumentTypeSuccess = (payload) => ({
  type: types.UPDATE_DOCUMENT_TYPE_SUCCESS,
  payload,
});

const updateDocumentTypeFailure = () => ({
  type: types.UPDATE_DOCUMENT_TYPE_FAILURE,
});

export const updateDocumentType = (payload) => async (dispatch) => {
  dispatch(updateDocumentTypeRequest());
  let response = null;
  if (payload?.id) {
    const { id, ...data } = payload;
    response = await documentsServices.updateDocumentType(id, data);
  } else {
    response = await documentsServices.createDocumentType(payload);
  }
  if (response?.ok) {
    dispatch(
      updateDocumentTypeSuccess({
        data: response?.data,
      })
    );
    return;
  }
  dispatch(updateDocumentTypeFailure());
  dispatch(
    uiActions.handleShowSnackbar({
      type: 'error',
      message: getErrorMessage(response?.data),
    })
  );
};

export const resetUpdateDocumentType = () => (dispatch) => {
  dispatch({
    type: types.UPDATE_DOCUMENT_TYPE_FAILURE,
  });
};

export const resetCurrentVersion = () => (dispatch) => {
  dispatch({
    type: types.RESET_CURRENT_DEFAULT_VERSION,
  });
};
